import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { useFormik } from "formik";
import { CreateCajaCurricularPciI } from "interfaces/CreateCajaCurricularPci";
import { EspacioCurricularI } from "interfaces/EspacioCurricular";
import { useEffect, useMemo, useState } from "react";
import { Button, Col, Collapse, Modal, Row, Spinner } from "react-bootstrap";
import { getCajasCurricularesByPlanEstudio } from "services/cajaCurricular";
import { getEspaciosCurricularesByCajaCurricular } from "services/espacioCurricular";
import { EyeIcon } from "assets/icons/EyeIcon";
import { LISTA_COLORES_ICONOS } from "assets/icons/LISTA_COLORES_ICONOS";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { EspaciosTable, EspaciosTableRow } from "components/EspaciosTable/EspaciosTable";
import { createCajaCurricularPciSchema } from "schemas/pci/createCajaCurricularPciSchema";

interface Props {
  actionType: "create" | "edit" | undefined;
  onClose?: any;
  elemento?: CreateCajaCurricularPciI;
  planEstudioId?: number | string;
  onSuccess?: (cajaCurricularPci: CreateCajaCurricularPciI) => void;
  showModal: boolean;
}

interface initialValuesI {
  caja_curricular_id: string | number;
  descripcion: string;
  version: string;
}

export const CajaCurricularPciABM = ({
  showModal,
  actionType,
  onClose,
  elemento,
  planEstudioId,
  onSuccess,
}: Props) => {
  const isEditMode = actionType == "edit";
  const [cajaCurricularOptions, setCajaCurricularOptions] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // --- Estado para preview de espacios de la caja base ---
  const [showEspaciosPreview, setShowEspaciosPreview] = useState(false);
  const [espaciosBase, setEspaciosBase] = useState<EspacioCurricularI[]>([]);
  const [loadingEspacios, setLoadingEspacios] = useState(false);

  const initialValues: initialValuesI = {
    caja_curricular_id: "",
    descripcion: "",
    version: "",
  };

  const fetchCajasCurriculares = async () => {
    if (!planEstudioId) return;
    try {
      setLoadingOptions(true);
      const cajasCurriculares = await getCajasCurricularesByPlanEstudio(planEstudioId as number);
      setCajaCurricularOptions(cajasCurriculares.elementos);
    } catch (error) {
      console.error("Error al obtener cajas curriculares:", error);
      showErrorToast("Error al obtener cajas curriculares");
    } finally {
      setLoadingOptions(false);
    }
  };

  const renderInitialValues = async () => {
    let currentValues;

    if (isEditMode && elemento) {
      currentValues = {
        caja_curricular_id: elemento?.caja_curricular_id ?? "",
        descripcion: elemento?.descripcion ?? "",
        version: elemento?.version ?? "",
      };
    } else {
      currentValues = initialValues;
    }
    setValues(currentValues);
  };

  const handleSubmitForm = async (params: initialValuesI) => {
    const newCaja: CreateCajaCurricularPciI = {
      ...(elemento?.id ? { id: elemento.id } : {}),
      caja_curricular_id: params.caja_curricular_id,
      descripcion: params.descripcion,
      version: params.version,
      espaciosCurricularesPci: elemento?.espaciosCurricularesPci ?? [],
    };
    if (onSuccess) onSuccess(newCaja);
    onClose();
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: createCajaCurricularPciSchema,
    onSubmit: handleSubmitForm,
  });

  // --- Handler para toggle del preview de espacios ---
  const handleToggleEspaciosPreview = async () => {
    if (showEspaciosPreview) {
      setShowEspaciosPreview(false);
      return;
    }

    if (!values.caja_curricular_id) return;

    try {
      setLoadingEspacios(true);
      setShowEspaciosPreview(true);
      const res = await getEspaciosCurricularesByCajaCurricular({
        caja_curricular_id: values.caja_curricular_id,
      });
      setEspaciosBase(res?.elementos ?? []);
    } catch (error) {
      console.error("Error al obtener espacios de la caja base:", error);
      setEspaciosBase([]);
    } finally {
      setLoadingEspacios(false);
    }
  };

  // --- Limpiar preview al cambiar la selección de caja ---
  const handleCajaChange = (e: any) => {
    setFieldValue("caja_curricular_id", e.target.value);
    setShowEspaciosPreview(false);
    setEspaciosBase([]);
  };

  // --- Mapear espacios base al formato de la tabla reutilizable ---
  const espaciosTableRows: EspaciosTableRow[] = useMemo(
    () =>
      [...espaciosBase]
        .sort((a, b) => (a.anio ?? 0) - (b.anio ?? 0))
        .map((esp) => ({
          id: esp.id,
          nombre: esp.nombre,
          anio: esp.anio,
          horasSemanales: esp.horas_semanales,
          cuatrimestre: esp.cuatrimestre ?? null,
          area: esp.area?.nombre,
          tipoEspacio: esp.tipo_espacio?.nombre,
        })),
    [espaciosBase]
  );

  useEffect(() => {
    resetForm();
    setShowEspaciosPreview(false);
    setEspaciosBase([]);
    if (actionType) {
      renderInitialValues();
      fetchCajasCurriculares();
    }
  }, [actionType]);

  return (
    <>
      <Modal show={showModal} onHide={onClose} centered size="lg">
        <Modal.Header>
          <Modal.Title>
            {isEditMode
              ? "Editar Caja Curricular PCI"
              : "Nueva Caja Curricular PCI"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Col>
              <Row>
                <Col sm={12} className="mb-3">
                  <div className="d-flex align-items-end gap-2">
                    <div className="flex-grow-1">
                      <Select
                        id="caja_curricular_id"
                        title="Caja Curricular"
                        size="lg"
                        optionsFormatConfig={{ value: "id", label: "nombre" }}
                        options={cajaCurricularOptions}
                        placeholder="Seleccionar"
                        variant="gray-container"
                        values={values}
                        errors={errors}
                        required
                        touched={touched}
                        isLoading={loadingOptions}
                        onChange={handleCajaChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    {values.caja_curricular_id && (
                      <Button
                        variant="secondary"
                        onClick={handleToggleEspaciosPreview}
                        title={showEspaciosPreview ? "Ocultar espacios de la caja base" : "Ver espacios de la caja base"}
                        style={{
                          height: "42px",
                          width: "42px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "0",
                        }}
                        aria-expanded={showEspaciosPreview}
                        aria-controls="espacios-preview-collapse"
                      >
                        <EyeIcon
                          size={20}
                          color={showEspaciosPreview ? LISTA_COLORES_ICONOS.BLUE_ACTIVO : LISTA_COLORES_ICONOS.DARK_GRAY}
                        />
                      </Button>
                    )}
                  </div>

                  <Collapse in={showEspaciosPreview}>
                    <div id="espacios-preview-collapse" className="mt-3">
                      {loadingEspacios ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Cargando espacios...
                        </div>
                      ) : (
                        <EspaciosTable
                          espacios={espaciosTableRows}
                          readonly
                          emptyMessage="No se encontraron espacios para esta caja curricular."
                        />
                      )}
                    </div>
                  </Collapse>
                </Col>
                <Col sm={12} className="mb-3">
                  <Input
                    id={"descripcion"}
                    title="Descripción"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={12} className="mb-3">
                  <Input
                    id={"version"}
                    title="Versión"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
              </Row>
            </Col>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="mx-3"
              variant="secondary"
              size="lg"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="user-submit-button"
              type="submit"
            >
              {isEditMode ? "Guardar Cambios" : "Confirmar"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
